import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";

interface EmployeeDetails {
  [key: string]: any;
}


interface EmployeeState {
  employeeDetails: EmployeeDetails;
  loading: boolean;
  error: string | null;
  /** True if last fetchEmployeeDetails failed with 404 (employee not found) */
  isEmployeeNotFound: boolean;
}

const initialState: EmployeeState = {
  employeeDetails: {},
  loading: false,
  error: null,
  isEmployeeNotFound: false,
};

export const fetchEmployeeDetails = createAsyncThunk(
  "employee/fetchEmployeeDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.ubt.employee.getEmployeeDetails);
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue({ message: "Request cancelled", isNotFound: false });
      }

      // Detect 404 (employee not found)
      const isNotFound = error?.response?.status === 404;
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.response?.data?.detail ??
        error?.response?.statusText ??
        error?.message ??
        "Failed to fetch employee details";

      return rejectWithValue({ message: errorMessage, isNotFound });
    }
  },
);


const employeeSlice = createSlice({
  name: "employee",
  initialState,
  reducers: {
    resetEmployeeDetailsState: (state) => {
      state.employeeDetails = {};
      state.loading = false;
      state.error = null;
      state.isEmployeeNotFound = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEmployeeDetails.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.isEmployeeNotFound = false;
    });
    builder.addCase(fetchEmployeeDetails.fulfilled, (state, action) => {
      state.loading = false;
      const payload: any = action.payload;
      state.employeeDetails = payload;
      state.isEmployeeNotFound = false;
    });
    builder.addCase(fetchEmployeeDetails.rejected, (state, action) => {
      state.loading = false;
      // If payload is an object with message/isNotFound, use those; else fallback for legacy
      const payload = action.payload as { message?: string; isNotFound?: boolean } | string | null | undefined;
      if (payload && typeof payload === 'object' && 'message' in payload) {
        state.error = typeof payload.message === 'string' ? payload.message : null;
        state.isEmployeeNotFound = !!payload.isNotFound;
      } else {
        state.error = payload as string;
        state.isEmployeeNotFound = false;
      }
    });
  },
});

export const { resetEmployeeDetailsState } = employeeSlice.actions;
export default employeeSlice.reducer;
